import { useQuery } from '@apollo/client'
import { useCallback } from 'react'
import { Params, useQueryWithParams } from 'hooks/useQueryWithParams'
import { findAllQuestion, findAllQuestionVariables } from '__generatedTypes__/findAllQuestion'
import { GET_LIST_DELETED_QUESTION, GET_LIST_QUESTION } from 'queries/question'
import { SearchTypeEnum } from '__generatedTypes__/globalTypes'
import { findAllDeletedQuestion, findAllDeletedQuestionVariables } from '__generatedTypes__/findAllDeletedQuestion'
import { QuestionFilters } from './useQuestionFilters'

export function castParams({ page, take, search, order, filters }: Params<QuestionFilters>): findAllQuestionVariables {
  const where: findAllQuestionVariables['params']['where'] = {
    isTemplate: false,
  }

  if (filters.questionCategory) {
    where.questionCategory = { id: filters.questionCategory.id }
  }

  if (filters.questionBankOrPracticeTest) {
    if (filters.questionBankOrPracticeTest.type === 'question-bank') {
      where.questionBank = { id: filters.questionBankOrPracticeTest.id }
    } else {
      where.practiceTest = { id: filters.questionBankOrPracticeTest.id }
    }
  }

  return {
    params: {
      skip: (page - 1) * take,
      take,
      search: search ? { query: search, type: SearchTypeEnum.ILike } : undefined,
      order: order?.field ? { [order.field]: order.direction } : undefined,
      where,
    },
  }
}

export function usePublishedQuestions(initialFilters?: Partial<QuestionFilters>) {
  return useQueryWithParams<
    findAllQuestion,
    findAllQuestionVariables,
    'findAllQuestion',
    'items',
    'count',
    QuestionFilters
  >(
    GET_LIST_QUESTION,
    'findAllQuestion',
    'items',
    'count',
    castParams,
    undefined,
    {
      fetchPolicy: 'network-only',
    },
    initialFilters ? { filters: initialFilters } : undefined
  )
}

export function useDeletedQuestions(initialFilters?: Partial<QuestionFilters>) {
  return useQueryWithParams<
    findAllDeletedQuestion,
    findAllDeletedQuestionVariables,
    'findAllDeletedQuestion',
    'items',
    'count',
    QuestionFilters
  >(
    GET_LIST_DELETED_QUESTION,
    'findAllDeletedQuestion',
    'items',
    'count',
    castParams,
    undefined,
    {
      fetchPolicy: 'network-only',
    },
    initialFilters ? { filters: initialFilters } : undefined
  )
}

export function useQuestionsCount() {
  const notTemplateVariables = {
    variables: {
      params: {
        where: {
          isTemplate: false,
        },
      },
    },
  }
  const publishedQuestionsState = useQuery<findAllQuestion, findAllQuestionVariables>(
    GET_LIST_QUESTION,
    notTemplateVariables
  )
  const deletedQuestionsState = useQuery<findAllDeletedQuestion, findAllDeletedQuestionVariables>(
    GET_LIST_DELETED_QUESTION,
    notTemplateVariables
  )

  const refetch = useCallback(() => {
    publishedQuestionsState.refetch()
    deletedQuestionsState.refetch()
  }, [])

  return {
    published: publishedQuestionsState.data?.findAllQuestion.count ?? 0,
    deleted: deletedQuestionsState.data?.findAllDeletedQuestion.count ?? 0,
    refetch,
  }
}
